import "./ReportInsightsData.scss";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Divider, Skeleton, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { RootState } from "../../../store/reducers";
import {
  JobPricingReportDetails,
  PriorYearDataModels
  } from "../../../helpers/models/JobPricingDetailModels";
import { useTranslation } from "react-i18next";
import { JobPricingContext } from "../../../store/JobPricingContext";

export const ReportInsightsData = () => {
  const { t } = useTranslation();
  const { setInsightsContentData } = useContext(JobPricingContext);
  const {
    targetData,
    reportDetails,
    previousYearReportDetails,
    isReportDetailsLoading
  } = useSelector((state: RootState) => state.jobPricingDetails);
  const emptyInsightsData: JobPricingReportDetails = {
    compensationFactKey: 0,
    factName: "",
    orgs: "",
    incumbents: "",
    marketPositions: "",
    marketValues: "",
    clientValues: "",
    orgEffectiveDate: "",
    marketEffectiveDate: "",
    targetMarket: 0,
    avgOrg: 0,
    orgAvgSalaryPercentage: 0,
    orgAvgSalaryValue: "",
    orgAvgSalaryStatus: ""
  };
  const [insightsData, setInsightsData] =
    useState<JobPricingReportDetails>(emptyInsightsData);
  const [priorYearData, setPriorYearData] = useState<PriorYearDataModels>({
    percentage: 0,
    percentageStatus: "",
    status: ""
  });

  const getInsightsPriorYearData = (
    currentYearData: JobPricingReportDetails
  ) => {
    let priorYearData = null;
    if (previousYearReportDetails && previousYearReportDetails.length > 0) {
      const previousYearData = previousYearReportDetails[0];
      const previousYearAvgMarket = Math.round(
        Number((previousYearData.marketValues || "").split("|")[0])
      );
      if (currentYearData.avgMarket && previousYearAvgMarket) {
        let percentage = Math.round(
          ((currentYearData.avgMarket - previousYearAvgMarket) /
            previousYearAvgMarket) *
            100
        );
        const percentageStatus = percentage > 0 ? "increase" : "decrease";
        percentage = percentage > 0 ? percentage : percentage * -1;
        let status = "";
        if (currentYearData.avgMarket > previousYearAvgMarket) {
          status = "trending up";
        } else if (currentYearData.avgMarket < previousYearAvgMarket) {
          status = "trending down";
        } else if (currentYearData.avgMarket === previousYearAvgMarket) {
          status = "steady";
        } else {
          status = "";
        }
        priorYearData = { percentage, percentageStatus, status };
        setPriorYearData(priorYearData);
      }
    }
    setInsightsContentData({
      currentYearData,
      previousYearData: priorYearData
    });
  };

  useEffect(() => {
    if (reportDetails && reportDetails.length) {
      setInsightsData(reportDetails[0]);
      getInsightsPriorYearData(reportDetails[0]);
    } else {
      setInsightsData(emptyInsightsData);
      setPriorYearData({
        percentage: 0,
        percentageStatus: "",
        status: ""
      });
      setInsightsContentData({
        currentYearData: null,
        previousYearData: null
      });
    }
  }, [reportDetails]);
  return !(
    !isReportDetailsLoading &&
    !insightsData.targetMarket &&
    !priorYearData.percentage
  ) ? (
    <div className="sjpr-insights-data-container">
      <div style={{ marginBottom: 10 }}>
        <AutoAwesomeIcon
          sx={{ color: "#8338EB", transform: "rotate(180deg)" }}
        />
        <Typography variant="subtitle1">{t("pa.insights")}</Typography>
      </div>
      {!(
        !isReportDetailsLoading &&
        !insightsData.targetMarket &&
        !insightsData.avgOrg
      ) && (
        <div>
          <WarningAmberRoundedIcon sx={{ color: "#8338EB" }} />
          {isReportDetailsLoading ? (
            <Box sx={{ width: "100%", marginLeft: 2 }}>
              <Skeleton />
              <Skeleton />
              <Skeleton width="60%" />
            </Box>
          ) : (
            <>
              {insightsData.avgOrg ? (
                <Typography variant="body2">
                  {t("pa.yourOrganizations")}{" "}
                  <b>
                    {insightsData.factName || "compensation"} is{" "}
                    {insightsData?.orgAvgSalaryPercentage}% (
                    {insightsData.orgAvgSalaryValue})
                  </b>{" "}
                  {insightsData.orgAvgSalaryStatus}{" "}
                  {t("pa.insightsDataAvgOrgDescriptionP1")}{" "}
                  <b>
                    {targetData.percentileDisplay}{" "}
                    {t("pa.insightsDataAvgOrgDescriptionP2")}
                  </b>
                </Typography>
              ) : (
                <Typography variant="body2">
                  {t("pa.insightsDataNonAvgOrgDescriptionP1")}{" "}
                  <b>{insightsData.factName || "compensation"}</b>{" "}
                  {t("pa.insightsDataNonAvgOrgDescriptionP2")}{" "}
                  <b>{insightsData.orgAvgSalaryValue}</b>
                </Typography>
              )}
            </>
          )}
        </div>
      )}
      {!(!isReportDetailsLoading && !priorYearData.percentage) && (
        <>
          <Divider flexItem sx={{ marginY: 2 }} />
          <div>
            <TrendingUpIcon sx={{ color: "#8338EB" }} />
            {isReportDetailsLoading ? (
              <Box sx={{ width: "100%", marginLeft: 2 }}>
                <Skeleton />
                <Skeleton />
                <Skeleton width="60%" />
              </Box>
            ) : (
              <Typography variant="body2">
                The{" "}
                <b>
                  {t("pa.medianAnnualSalary")} {priorYearData.percentageStatus}{" "}
                  is {priorYearData.percentage}%
                </b>{" "}
                {t("pa.reportsInsightsDescription")} {priorYearData.status}.
              </Typography>
            )}
          </div>
        </>
      )}
    </div>
  ) : (
    <></>
  );
};
